/**
 * useBookingDimensions — helper chung cho luồng đặt lịch (Booking Policy).
 *
 * Đọc cấu hình `/api/v1/booking-policy` một lần rồi trả về 4 chiều
 * (Khoa / Phòng / Bác sĩ / Dịch vụ) đã resolve theo THỨ TỰ cấu hình, mỗi chiều
 * kèm cờ {visible, required}. Mọi màn đặt lịch trong admin (và mini app / website
 * sau này) dùng chung helper này để hiện/ẩn/bắt buộc đồng nhất — thay vì mỗi
 * trang tự đọc `dimDepartment`/`dimRoom`/… một kiểu.
 *
 * HIDDEN   → không hiện với người đặt (hệ thống/lễ tân xử lý sau).
 * OPTIONAL → hiện, được phép bỏ trống.
 * REQUIRED → hiện, chặn submit nếu bỏ trống.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  bookingPolicyApi,
  bookingPolicyQueryKeys,
  type BookingDimensionKey,
  type BookingPolicy,
  type DimensionMode,
} from "../api/bookingPolicy";

export interface BookingDimension {
  key: BookingDimensionKey;
  /** Nhãn tiếng Việt ("Khoa" / "Phòng" / "Bác sĩ" / "Dịch vụ"). */
  label: string;
  mode: DimensionMode;
  /** mode !== HIDDEN. */
  visible: boolean;
  /** mode === REQUIRED. */
  required: boolean;
  /** Vị trí trong thứ tự cấu hình (0-based). */
  order: number;
}

const DIM_LABEL: Record<BookingDimensionKey, string> = {
  department: "Khoa",
  room: "Phòng",
  doctor: "Bác sĩ",
  service: "Dịch vụ",
};

const DIM_MODE_KEY: Record<BookingDimensionKey, keyof BookingPolicy> = {
  department: "dimDepartment",
  room: "dimRoom",
  doctor: "dimDoctor",
  service: "dimService",
};

/** Thứ tự mặc định khi policy chưa cấu hình `dimOrder`. */
export const DEFAULT_DIMENSION_ORDER: BookingDimensionKey[] = [
  "department",
  "room",
  "doctor",
  "service",
];

/**
 * Chuyển một BookingPolicy thô thành danh sách 4 chiều đã sắp thứ tự + cờ hiển thị.
 * Pure function (không dùng hook) để test được và tái dùng ngoài React.
 */
export function resolveBookingDimensions(
  policy: BookingPolicy
): BookingDimension[] {
  const configured = (policy.dimOrder ?? []).filter(
    (k, i, arr) => DIM_LABEL[k] !== undefined && arr.indexOf(k) === i
  );
  // Chiều nào thiếu trong dimOrder → nối vào cuối theo thứ tự mặc định.
  const order: BookingDimensionKey[] = [
    ...configured,
    ...DEFAULT_DIMENSION_ORDER.filter((k) => !configured.includes(k)),
  ];
  return order.map((key, i) => {
    const mode = policy[DIM_MODE_KEY[key]] as DimensionMode;
    return {
      key,
      label: DIM_LABEL[key],
      mode,
      visible: mode !== "HIDDEN",
      required: mode === "REQUIRED",
      order: i,
    };
  });
}

/** Giá trị hiện tại của 4 chiều để chấm điểm bắt buộc. */
export type BookingDimensionValues = Partial<
  Record<BookingDimensionKey, string | null | undefined>
>;

/**
 * Trả về map lỗi { key: message } cho các chiều REQUIRED đang bỏ trống & đang hiển thị.
 * Chiều HIDDEN không bao giờ báo lỗi (người đặt không thấy nó).
 */
export function validateBookingDimensions(
  dimensions: BookingDimension[],
  values: BookingDimensionValues
): Partial<Record<BookingDimensionKey, string>> {
  const errors: Partial<Record<BookingDimensionKey, string>> = {};
  for (const d of dimensions) {
    if (d.visible && d.required && !values[d.key]) {
      errors[d.key] = `Vui lòng chọn ${d.label.toLowerCase()}`;
    }
  }
  return errors;
}

export interface UseBookingDimensionsResult {
  policy: BookingPolicy | undefined;
  /** 4 chiều theo thứ tự cấu hình (kể cả chiều HIDDEN — lọc bằng `.visible` khi render). */
  dimensions: BookingDimension[];
  /** Tra cứu nhanh theo khoá. */
  byKey: Record<BookingDimensionKey, BookingDimension | undefined>;
  isLoading: boolean;
  isError: boolean;
}

/** Hook: đọc booking policy + resolve 4 chiều. Cache chung với trang cấu hình. */
export function useBookingDimensions(): UseBookingDimensionsResult {
  const q = useQuery({
    queryKey: bookingPolicyQueryKeys.all(),
    queryFn: () => bookingPolicyApi.get(),
    staleTime: 5 * 60_000,
  });

  const dimensions = useMemo(
    () => (q.data ? resolveBookingDimensions(q.data) : []),
    [q.data]
  );

  const byKey = useMemo(() => {
    const m = {} as Record<BookingDimensionKey, BookingDimension | undefined>;
    for (const d of dimensions) m[d.key] = d;
    return m;
  }, [dimensions]);

  return {
    policy: q.data,
    dimensions,
    byKey,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
